// A STANDING STATEMENT, not a ledger. The partners never transfer money to each other,
// so this screen records nothing and offers no action: it states where the two of them
// stand right now, and every figure moves only when a payment is collected, an expense
// is paid, or a share changes.
//
// FOUR CARDS, AND THE FIRST TWO ANSWER DIFFERENT QUESTIONS ABOUT DIFFERENT TIMESPANS.
// Keeping them apart is the main thing this file is responsible for:
//
//   1. this month's collection   what came in during ONE month and to whom. A plain
//                                spread between two figures. Not the gap.
//   2. the cumulative gap        the standing all-time position: what each holds
//                                against what their share entitles them to. Balances to
//                                zero only over all time, so it has NO monthly value.
//   3. what each collected       the list behind the gap's collected line, all-time
//   4. cash by month             the full month-by-month ledger, in and out
//
// THE MONTH SELECTOR CANNOT MOVE THE GAP, STRUCTURALLY. `month` is local state and is
// deliberately NOT in the `useAsync` deps below — that hook re-runs solely on `deps`
// (it reads the loader through a ref and its `run` is memoised on an empty array), so
// changing months re-renders and never re-fetches. The gap is rendered from a `balance`
// object fetched once on mount, and there is no code path from the arrows to it.
//
// (Before this, card 2 was card 1 and answered the whole screen. The per-unit sources
// that used to sit under it — a seven-column table behind a toggle — are gone; the
// calculator is untouched, see api/rest/settlements/service.js.)
//
// THE CLIENT ADDS NOTHING. Every figure is a field the server computed and asserted.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { settlementsApi } from '../../api';
import he from '../../i18n/he';
import { currentMonthStr } from '../../utils/dates';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Money, { Cost } from '../../components/Money';
import MonthlyCollection from './MonthlyCollection';
import PartnerStatement from './PartnerStatement';
import CollectedList from './CollectedList';
import OwedToStaff from './OwedToStaff';
import MonthlyCashflow from './MonthlyCashflow';

// The one sentence that matters: which way the gap runs, and how big it is. Deliberately
// NOT "X owes Y" — nobody owes anybody and nothing will be paid, so the sentence names a
// direction, not a debt.
const Headline = ({ summary }) => {
  if (!summary || !summary.amount) {
    return <p className="settle-headline muted">{he.settlements.noGap}</p>;
  }
  return (
    <p className="settle-headline">
      {he.settlements.gapFavours}{' '}
      <strong>{summary.favoursName}</strong>{' '}
      <strong><Money value={summary.amount} /></strong>{' '}
      <span className="muted">{`${he.settlements.gapAgainst} ${summary.otherName}`}</span>
    </p>
  );
};

export default function Settlements() {
  // Two requests, not one: the monthly view grows with every month the business runs,
  // and the gap above never needs it. They are fetched together because this is the one
  // screen that shows both.
  const { data, loading, refreshing, error } = useAsync(() =>
    Promise.all([settlementsApi.balance(), settlementsApi.monthly()])
  );
  const [balance, monthly] = data || [null, null];
  // The month the screen leads with. Local state and nothing more — see the note at the
  // top of this file: it is absent from the deps above on purpose, so stepping through
  // months never re-reads the balance and the gap below cannot move with it.
  const [month, setMonth] = useState(currentMonthStr());

  return (
    <>
      <PageHeader title={he.settlements.title} />
      <AsyncBoundary loading={loading} error={error}>
        {balance && (
          <>
            {/* ONE MONTH'S COLLECTION — the first thing on the screen, and a different
                question from everything below it. See MonthlyCollection.js. */}
            {monthly && (
              <MonthlyCollection
                byMonth={monthly.byMonth}
                partners={balance.perPartner}
                month={month}
                onMonthChange={setMonth}
              />
            )}

            <div className={`card settle-card${refreshing ? ' refreshing' : ''}`}>
              {/* THE CUMULATIVE POSITION, named as such in its own heading. Everything in
                  this card is all-time and none of it reads the month selected above. */}
              <h2>{he.settlements.gapTitle}</h2>
              <Headline summary={balance.summary} />

              {/* The whole answer: five lines each, side by side. */}
              <div className="settle-statements">
                {balance.perPartner.map((p) => (
                  <PartnerStatement key={p.partnerId} partner={p} />
                ))}
              </div>

              {/* The accrued figure, as ONE line. It answers "what have I made", which
                  the cash gap never does — and without it the gap reads as profit. */}
              <p className="muted small-note settle-aside">
                {he.settlements.earnedLine}:{' '}
                {balance.perPartner.map((p, i) => (
                  <span key={p.partnerId}>
                    {i > 0 && ' · '}
                    {p.name} <Money value={p.earned} />
                  </span>
                ))}
              </p>

              {/* Costs committed but not yet paid. Shown precisely because they are NOT
                  in any figure above: without this line a partner records a real expense,
                  watches the gap stay put, and assumes it was lost. */}
              {balance.unpaidCost > 0 && (
                <p className="muted small-note settle-aside">
                  {he.settlements.unpaidExpenses}:{' '}
                  <strong><Cost value={balance.unpaidCost} /></strong>
                </p>
              )}
            </div>

            {/* WHO ELSE WE ARE OUT OF POCKET TO — beside the gap, never inside it. The
                gap is an offset between the two OWNERS and balances to zero; this is a
                liability to somebody who holds no share and is in no profit, so it is a
                different number about different people. It renders itself away when
                nobody is owed. See ./OwedToStaff.js for why it cannot double-count with
                the card above. */}
            <OwedToStaff rows={balance.owedToStaff} />

            {/* The list behind each statement's first line — its own card, because it is
                the answer to a second question and not a fold inside the first. */}
            <div className={`card settle-card${refreshing ? ' refreshing' : ''}`}>
              <h2>{he.settlements.collectedList.title}</h2>
              <div className="settle-collected-lists">
                {balance.perPartner.map((p) => (
                  <CollectedList key={p.partnerId} partner={p} />
                ))}
              </div>
            </div>

            {/* A different question again, so its own card and closed by default: cash
                that actually moved, per month. The gap is cumulative and has no monthly
                value, and keeping the two apart is what stops this from being read as
                "the gap, broken down by month". */}
            {monthly && (
              <div className={`card settle-card${refreshing ? ' refreshing' : ''}`}>
                <h2>{he.settlements.monthly.title}</h2>
                {monthly.perPartner.map((p) => (
                  <MonthlyCashflow key={p.partnerId} partner={p} />
                ))}
              </div>
            )}
          </>
        )}
      </AsyncBoundary>
    </>
  );
}
